import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import SectionTitle from '../components/SectionTitle';
import ContactForm from '../components/ContactForm';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';
import Icon from '../components/Icon';

// Import content
import siteContent from '../content/site.json';

/**
 * Contact page component
 * @returns {React.ReactElement} Contact page
 */
const ContactPage = () => {
  // Get contact info from site.json
  const { contact: contactInfo } = siteContent;

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Header />
      
      {/* Page Header */}
      <section className="pt-40 md:pt-48 lg:pt-56 pb-12 bg-brand-black text-white">
        <Container>
          <SectionTitle 
            title="Contact Us" 
            subtitle="Get in touch to discuss your project or request a quote"
            className="mb-0"
          />
        </Container>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 md:py-24 bg-brand-black text-white">
        <Container>
          <div className="grid md:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="md:col-span-2">
              <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>
              <ComponentErrorBoundary 
                fallbackMessage="The contact form is temporarily unavailable. Please email us directly at info@lonestaraerialsolutions.com or call us."
                componentName="ContactForm"
                showRetry={true}
              >
                <ContactForm />
              </ComponentErrorBoundary>
            </div>
            
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
              <div className="bg-white rounded-2xl shadow-card p-8 text-brand-black">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-lg mb-4">Get in Touch</h3>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Icon name="mail" className="mr-3 text-brand-blue-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-medium">Email</p>
                          <a 
                            href={`mailto:${contactInfo.email}`} 
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                          >
                            {contactInfo.email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Icon name="phone" className="mr-3 text-brand-blue-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-medium">Phone</p>
                          <a 
                            href={`tel:${contactInfo.phone.replace(/[^0-9]/g, '')}`} 
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                          >
                            {contactInfo.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg mb-4">Service Area</h3>
                    <div className="flex items-start">
                      <Icon name="pin" className="mr-3 text-brand-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <p>{contactInfo.serviceArea}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          We provide drone services throughout Texas, including Austin, 
                          Houston, Dallas, San Antonio, and surrounding areas.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg mb-4">Response Time</h3>
                    <p>
                      We typically respond to all email inquiries within 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-brand-black text-white">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Have More Questions?</h2>
            <p className="mb-6">Check out our <a href="/faq" className="text-brand-blue-600 hover:text-brand-blue-500 underline">FAQ page</a> for answers to common questions about our services.</p>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;